/**
 * Two questions on one page, and they are different questions.
 *
 * **What was spent** - computed from the latest reading of each
 * document, because three attempts at one invoice is still one invoice.
 * Money is shown one currency at a time: a single total that adds euros
 * to yen is a number with no meaning presented as confidently as one
 * with meaning, and a dashboard is exactly where that gets screenshotted.
 *
 * **How well it is being read** - validation pass rate by vendor and the
 * live failure taxonomy. This is the half an ordinary invoicing tool
 * does not have, and it is the half that says whether the first half can
 * be trusted.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "./api";
import { BarsH, BarsV, ChartCard, STATUS, type Datum } from "./charts";
import { groupDigits } from "./format";
import type { Analytics } from "./types";
import { Chip, Icon, Tile } from "./ui";

export function AnalyticsView() {
  const [data, setData] = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>("");
  const [source, setSource] = useState<string>("");

  const load = useCallback(async () => {
    try {
      setData(
        await api.analytics({
          currency: currency || undefined,
          source: source || undefined,
        }),
      );
      setError(null);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    }
  }, [currency, source]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="page">
        <div className="banner">{Icon.alert({ size: 17 })} {error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <div className="tiles">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 92 }} />
          ))}
        </div>
      </div>
    );
  }

  const drawn = data.currency;
  const pct = (value: number | null) =>
    value === null ? "—" : `${(value * 100).toFixed(1)}%`;

  const vendorSpend: Datum[] = data.spend_by_vendor.map((b) => ({
    label: b.label,
    value: Number(b.value),
    count: b.count,
  }));

  const monthly: Datum[] = data.spend_by_month.map((b) => ({
    label: b.label,
    value: Number(b.value),
    count: b.count,
  }));

  // Worst first, and coloured by how bad: this is a status scale, not a
  // categorical one, so it uses the reserved status hues and every bar
  // is directly labelled with its rate.
  const reliability: Datum[] = data.reliability_by_vendor.map((b) => {
    const rate = Number(b.value);
    return {
      label: b.label,
      value: rate,
      count: b.count,
      color: rate >= 0.95 ? STATUS.ok : rate >= 0.8 ? STATUS.warn : STATUS.danger,
      display: `${(rate * 100).toFixed(0)}%`,
    };
  });

  const findings: Datum[] = data.findings.map((b) => ({
    label: b.label.replace(/_/g, " "),
    value: b.count,
    count: b.count,
    // value carries a 1 for errors and 0 for warnings, so the taxonomy
    // keeps the severity distinction the rule codes were built on.
    color: b.value === "1" ? STATUS.danger : STATUS.warn,
    display: String(b.count),
  }));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Analytics</h1>
          <p>
            Two questions, kept apart: what was invoiced, and how reliably it was
            read. Every figure here comes from the latest reading of each document,
            so re-reading an invoice never inflates a total.
          </p>
          {/* Every panel is filtered alike, so the scope has to be stated
              once, plainly, where it is read. A dashboard showing two
              documents beside a count taken over the whole database is
              answering a question nobody asked. */}
          <p className="scope-note">
            {source ? (
              <>
                Showing <strong>{source}</strong> documents only. Every number
                below, readings included, is counted over this slice.
              </>
            ) : (
              <>Showing every document. {data.documents} in scope.</>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select
            className="field-control"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            aria-label="Filter by source"
          >
            <option value="">Every source</option>
            <option value="upload">Uploaded only</option>
            <option value="sample">Samples only</option>
            <option value="synthetic">Synthetic only</option>
          </select>
          <select
            className="field-control"
            // Reflects the currency actually drawn, not the empty
            // default: the server picks the busiest currency when none
            // is chosen, and a picker showing something else while the
            // charts are drawn in another is a lie about the data.
            value={currency || drawn || ""}
            onChange={(event) => setCurrency(event.target.value)}
            aria-label="Currency for the money panels"
          >
            {data.currencies.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tiles">
        <Tile
          label={source ? `Documents (${source})` : "Documents"}
          value={data.documents}
          foot={`${data.extractions} readings of these documents`}
        />
        <Tile
          label="Validation pass"
          value={pct(data.validation_pass_rate)}
          tone="accent"
          foot="documents whose own numbers agree"
        />
        <Tile
          label="Needs review"
          value={data.needs_review}
          tone={data.needs_review ? "warn" : undefined}
          foot={`${data.approved} approved by a person`}
        />
        <Tile
          label="Unreadable"
          value={data.unreadable}
          tone={data.unreadable ? "danger" : undefined}
          foot="nothing could be extracted"
        />
        <Tile
          label="Fields filled"
          value={pct(data.field_completeness)}
          foot="number, date, currency, vendor, total"
        />
      </div>

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-head">
          <div>
            <h2>Invoiced value</h2>
            <p>
              One row per currency, never one total. Adding euros to yen produces a
              number that means nothing, and it would look exactly as authoritative
              as one that means something.
            </p>
            <p>
              <strong>Extracted is not approved.</strong> The extracted column
              includes every document that was read, including the ones sitting
              in the queue because their own arithmetic disagrees. The approved
              column is what a person has actually signed off. The gap between
              them is the review queue.
            </p>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Currency</th>
                <th className="right">Documents</th>
                <th className="right">Extracted</th>
                <th className="right">Approved</th>
              </tr>
            </thead>
            <tbody>
              {data.spend.map((row) => (
                <tr key={row.currency} style={{ cursor: "default" }}>
                  <td>
                    <Chip tone={row.currency === drawn ? "info" : "neutral"}>
                      {row.currency}
                    </Chip>
                  </td>
                  <td className="right num">{row.documents}</td>
                  <td className="right num strong">{groupDigits(row.total)}</td>
                  <td className="right num">
                    {groupDigits(row.approved_total)}
                    <span className="sub">
                      {row.approved_documents} of {row.documents}
                    </span>
                  </td>
                </tr>
              ))}
              {data.spend.length === 0 && (
                <tr>
                  <td colSpan={4} className="note">
                    No totals were read yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="charts">
        <ChartCard
          title="Invoiced by month"
          subtitle={drawn ? `${drawn}, by issue date` : undefined}
          data={monthly}
          valueHeader={drawn ?? "value"}
        >
          <BarsV data={monthly} currency={drawn} />
        </ChartCard>

        <ChartCard
          title="Top vendors"
          subtitle={drawn ? `${drawn}, latest reading of each document` : undefined}
          data={vendorSpend}
          valueHeader={drawn ?? "value"}
        >
          <BarsH data={vendorSpend} />
        </ChartCard>

        <ChartCard
          title="Validation pass rate by vendor"
          subtitle="Worst first. Vendors with fewer than two documents are excluded — one document is an anecdote, not a rate."
          data={reliability}
          valueHeader="pass rate"
        >
          <BarsH data={reliability} formatValue={(v) => `${(v * 100).toFixed(0)}%`} />
          <div className="legend">
            <span>
              <i style={{ background: STATUS.ok }} /> 95% and above
            </span>
            <span>
              <i style={{ background: STATUS.warn }} /> 80–95%
            </span>
            <span>
              <i style={{ background: STATUS.danger }} /> below 80%
            </span>
          </div>
        </ChartCard>

        <ChartCard
          title="Failure taxonomy"
          subtitle="Which rule fired, across the latest reading of every document."
          data={findings}
          valueHeader="documents"
        >
          <BarsH data={findings} labelWidth={168} formatValue={(v) => String(v)} />
          <div className="legend">
            <span>
              <i style={{ background: STATUS.danger }} /> error — the document
              contradicts itself
            </span>
            <span>
              <i style={{ background: STATUS.warn }} /> warning — worth a glance
            </span>
          </div>
        </ChartCard>
      </div>

      <p className="note" style={{ marginTop: 16 }}>
        Validation pass rate needs no ground truth: it asks only whether a
        document's own numbers agree with each other, which is what a production
        system can actually measure. Accuracy against a known answer is a separate
        figure and it lives in the evaluation harness, where the answers exist.
      </p>
    </div>
  );
}
